import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiCalendar,
  FiDownload,
  FiExternalLink,
  FiUser,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useGetNewsMediaPublicationQuery } from "../../features/cmsApi";
import BreadcrumbComponent from "./BreadcrumbComponent";
import PdfDialog from "./TheClub/PdfDialog";
import { cmsBaseUrl } from "../../constants/baseUrl";

interface Publication {
  id: number;
  title: string;
  details?: string;
  publicationType?: string;
  author?: string | null;
  publishedDate: string;
  category?: string | null;
  image?: string;
  attachment?: string;
  link?: string | null;
  featured?: boolean;
  thumbnail?: {
    url: string;
  };
  file?: {
    url: string;
  };
}

function PublicationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data } = useGetNewsMediaPublicationQuery();
  const [publication, setPublication] = useState<Publication | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (data?.data?.publication && id) {
      const foundPublication = data.data.publication.find(
        (item: Publication) => item.id.toString() === id
      );
      setPublication(foundPublication || null);
      setLoading(false);
    }
  }, [data, id]);

  const handleGoBack = () => {
    navigate("/IBAXIS");
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case "Annual Report":
        return "blue";
      case "Journal":
        return "green";
      case "Newsletter":
        return "purple";
      case "Magazine":
        return "orange";
      case "Report":
        return "teal";
      default:
        return "gray";
    }
  };

  if (loading) {
    return (
      <Container maxW="4xl" py={12}>
        <Box textAlign="center" py={20}>
          <Text fontSize="lg" color="gray.500">
            Loading publication details...
          </Text>
        </Box>
      </Container>
    );
  }

  if (!publication) {
    return (
      <Container maxW={"4xl"} py={12}>
        <Box textAlign="center" py={20}>
          <Text fontSize="6xl" opacity={0.3} mb={6}>
            📚
          </Text>
          <Heading size="lg" color="gray.600" mb={4}>
            Publication Not Found
          </Heading>
          <Text color="gray.500" mb={8}>
            The publication you're looking for doesn't exist or has been
            removed.
          </Text>
          <Button colorScheme="blue" onClick={handleGoBack}>
            <Icon mr={2}>
              <FiArrowLeft />
            </Icon>
            Back to Publications
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW={"4xl"} py={8}>
      {/* Breadcrumb and Back Button */}
      <Box mb={8}>
        <Flex justify="space-between" align="center" mb={4}>
          <BreadcrumbComponent
            data={[
              { title: "Home", href: "/" },
              { title: "IBAXIS", href: "/IBAXIS" },
              { title: publication.title.substring(0, 30) + "...", href: null },
            ]}
          />
        </Flex>
      </Box>

      {/* Publication Article */}
      <Box
        bg="white"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="xl"
        border="1px"
        borderColor="gray.200"
      >
        {/* Title Section */}
        <Box px={{ base: 6, md: 12 }} pt={{ base: 8, md: 12 }} pb={6}>
          <VStack alignItems="start" gap={4}>
            {publication.featured && (
              <Badge
                colorScheme="red"
                variant="solid"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                ⭐ Featured Publication
              </Badge>
            )}

            <Heading
              size={{ base: "lg", md: "xl", lg: "2xl" }}
              lineHeight="1.2"
              color="#090047"
              fontWeight="bold"
            >
              {publication.title}
            </Heading>

            <HStack gap={6} flexWrap="wrap">
              <Badge
                colorScheme={getTypeColor(publication.publicationType)}
                variant="subtle"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
              >
                {publication.publicationType ||
                  publication.category ||
                  "Publication"}
              </Badge>

              <HStack color="gray.500" fontSize="sm">
                <Icon>
                  <FiCalendar />
                </Icon>
                <Text fontWeight="medium">
                  {new Date(publication.publishedDate).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </Text>
              </HStack>

              {publication.author && (
                <HStack color="gray.500" fontSize="sm">
                  <Icon>
                    <FiUser />
                  </Icon>
                  <Text fontWeight="medium">By {publication.author}</Text>
                </HStack>
              )}
            </HStack>
          </VStack>
        </Box>

        {/* Image Section */}
        {publication?.thumbnail?.url ? (
          <Avatar.Root
            shape="square"
            w="100%"
            h={{ base: "180px", md: "200px", lg: "220px" }}
            borderTopRadius="xl"
            bg="gray.50"
          >
            <Avatar.Fallback
              name={publication.title}
              bg="gray.100"
              color="gray.500"
            />
            <Avatar.Image
              src={`${import.meta.env.VITE_CMS_API_URL}${publication?.thumbnail?.url
                }`}
              objectFit="contain"
              w="100%"
              h="100%"
            />
          </Avatar.Root>
        ) : (
          <Avatar.Root
            shape="square"
            w="100%"
            h={{ base: "180px", md: "200px", lg: "220px" }}
            bg="gray.100"
            borderTopRadius="xl"
          >
            <Avatar.Fallback
              name={publication.title}
              bg="gray.100"
              color="gray.400"
              w="100%"
              h="100%"
            >
              <Text fontSize="4xl">📰</Text>
            </Avatar.Fallback>
          </Avatar.Root>
        )}

        <Separator />

        {/* Content Section */}
        <Box px={{ base: 6, md: 12 }} py={{ base: 8, md: 12 }}>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.8"
            color="gray.700"
            whiteSpace="pre-line"
            mb={8}
            textAlign="justify"
          >
            {publication.details ||
              "No detailed content available for this publication."}
          </Text>
          <Box flexShrink={0}>
            {publication.file && publication.file.url && <PdfDialog
              title={publication.title}
              pdfUrl={`${cmsBaseUrl}${publication.file.url}`}
            >
              <Button
                size="sm"
                bg="blue.900"
                color="white"
                _hover={{ bg: "blue.700" }}
                rounded={"full"}
                px={8}
              >
                {/* <Icon as={FaEye} mr={2} /> */}
                View Resource File
              </Button>
            </PdfDialog>}
          </Box>

          {/* Action Buttons */}
          {(publication.attachment || publication.link) && (
            <>
              <Separator mb={8} />
              <VStack alignItems="start" gap={4}>
                <Text fontSize="lg" fontWeight="semibold" color="#090047">
                  Resources & Downloads
                </Text>
                <HStack gap={4} flexWrap="wrap">
                  {publication.attachment && (
                    <Link
                      href={publication.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      _hover={{ textDecoration: "none" }}
                    >
                      <Button
                        colorScheme="blue"
                        variant="solid"
                        borderRadius="full"
                        size="md"
                        _hover={{
                          bg: "blue.600",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        <Icon mr={2}>
                          <FiDownload />
                        </Icon>
                        Download PDF
                      </Button>
                    </Link>
                  )}

                  {publication.link && (
                    <Link
                      href={publication.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      _hover={{ textDecoration: "none" }}
                    >
                      <Button
                        colorScheme="teal"
                        variant="outline"
                        borderRadius="full"
                        size="md"
                        _hover={{
                          bg: "teal.50",
                          borderColor: "teal.300",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        <Icon mr={2}>
                          <FiExternalLink />
                        </Icon>
                        Visit External Link
                      </Button>
                    </Link>
                  )}
                </HStack>
              </VStack>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box bg="gray.50" px={{ base: 6, md: 12 }} py={6}>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <VStack alignItems="start" gap={1}>
              <Text fontSize="sm" color="gray.500">
                Published on{" "}
                {new Date(publication.publishedDate).toLocaleDateString()}
              </Text>
              {publication.author && (
                <Text fontSize="sm" color="gray.500">
                  Author: {publication.author}
                </Text>
              )}
            </VStack>
          </Flex>
        </Box>
      </Box>
    </Container>
  );
}

export default PublicationDetails;
